import useGlobalContext from '$hooks/use-global-context';
import { optionVisibilityInitial } from '$context/global-context/reducers/coBrandDetails';
import { isOptionVisible } from '$utils/option-visibility';
import type { OptionVisibilityState } from '$context/global-context/reducers/types';
import type { ApplicantPortalOption } from '$constants/applicantPortalOption';

/**
 * Reads `coBrandDetails.optionVisibility` from GlobalContext. No network call —
 * the config arrives with the CoBrandDetails payload the portal already fetches.
 *
 * An unresolved or absent context falls back to `optionVisibilityInitial`, which
 * hides nothing, so every option shows (fail-open).
 *
 * @returns the raw visibility state and a predicate for a single option
 */
export const useOptionVisibility = (): {
  optionVisibility: OptionVisibilityState;
  isVisible: (option: ApplicantPortalOption) => boolean;
} => {
  const { state } = useGlobalContext();
  /* `state` is optional-chained so the documented absent-context fallback holds
     during onboarding, before much of the context exists. */
  const optionVisibility =
    state?.coBrandDetails?.optionVisibility ?? optionVisibilityInitial;

  return {
    optionVisibility,
    isVisible: (option) => isOptionVisible(option, optionVisibility)
  };
};
